import { useState } from "react";
import { AccAddress, ValAddress } from "@terra-money/terra.js";
import Card from "../../components/Card";
import Address from "../../components/Address";
import Icon from "../../components/Icon";
import Loading from "../../components/Loading";
import useRequest from "../../hooks/useRequest";
import { useGetQueryURL } from "../../queries/query";
import { useCurrentChain } from "../../contexts/ChainsContext";
import s from "./BlockEvents.module.scss";

interface Props {
  height: number;
}

interface BlockEventsResponse {
  events: BlockEvent[];
}

const parseValue = (str: string) =>
  AccAddress.validate(str) || ValAddress.validate(str) ? (
    <Address address={str} />
  ) : (
    str
  );

const BlockEvents = ({ height }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { chainID } = useCurrentChain();
  const queryURL = useGetQueryURL(
    `/v1/blocks/${height}/events?chainId=${chainID}`
  );

  const { data, isLoading, isError } = useRequest<BlockEventsResponse>({
    url: queryURL,
    enabled: isOpen
  });

  const events = data?.events || [];

  // Group events by scope
  const eventsByScope = events.reduce((acc, event) => {
    const scope = event.scope || "unknown";
    if (!acc[scope]) {
      acc[scope] = [];
    }
    acc[scope].push(event);
    return acc;
  }, {} as Record<string, BlockEvent[]>);

  const scopes = Object.keys(eventsByScope);

  return (
    <Card
      title={
        <div className={s.header}>
          <span>
            Block Events{events.length > 0 ? ` (${events.length})` : ""}
          </span>
          <button className={s.toggleButton} onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? "Hide" : "Show"}
            <Icon name={isOpen ? "expand_less" : "expand_more"} size={18} />
          </button>
        </div>
      }
      bordered
    >
      {isOpen && (
        <div className={s.eventsContainer}>
          {isLoading ? (
            <Loading />
          ) : isError ? (
            <p className={s.errorText}>Failed to load block events.</p>
          ) : events.length === 0 ? (
            <p className={s.emptyText}>No events for this block.</p>
          ) : (
            scopes.map(scope => (
              <div key={scope} className={s.stageSection}>
                <h3 className={s.stageName}>{scope}</h3>
                {eventsByScope[scope].map((event, eventIndex) => (
                  <div key={eventIndex} className={s.eventBox}>
                    <h4 className={s.eventType}>{event.type}</h4>
                    <table className={s.attributes}>
                      <tbody>
                        {event.attributes.map((attr, attrIndex) => (
                          <tr key={attrIndex}>
                            <th className={s.attrKey}>{attr.key}</th>
                            <td className={s.attrValue}>
                              {attr.value ? parseValue(attr.value) : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </Card>
  );
};

export default BlockEvents;
